/**
 * Comprehensive cache clearing utilities for aggressive PWA updates
 */

// Clear all service worker caches
export async function clearServiceWorkerCaches(): Promise<boolean> {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('[CacheUtils] Cleared all SW caches:', cacheNames);
      return true;
    } catch (error) {
      console.error('[CacheUtils] Failed to clear caches:', error);
      return false;
    }
  }
  return false;
}

// Unregister all service workers
export async function unregisterServiceWorkers(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      console.log('[CacheUtils] Unregistered all service workers');
      return true;
    } catch (error) {
      console.error('[CacheUtils] Failed to unregister SW:', error);
      return false;
    }
  }
  return false;
}

// Clear app-specific localStorage cache entries (not game data)
export function clearAppCacheData(): void {
  const cacheKeys = [
    'wordpop-build-hash',
    'wordpop-update-dismissed-session',
    'wordpop-stats-last-check',
    'wordpop-pwa-prompt-shown',
  ];
  
  cacheKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear all sessionStorage
  sessionStorage.clear();
  
  console.log('[CacheUtils] Cleared app cache data');
}

// Force refresh with cache bypass
export function forceHardRefresh(): void {
  // Clear location with cache bypass
  if ('location' in window) {
    // Add timestamp to bust any remaining caches
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  }
}

// Complete cache clear and refresh (nuclear option)
export async function clearAllCachesAndRefresh(): Promise<void> {
  console.log('[CacheUtils] Starting complete cache clear...');
  
  // Set a failsafe timeout - refresh no matter what after 3 seconds
  const failsafeTimeout = setTimeout(() => {
    console.log('[CacheUtils] Failsafe timeout triggered, forcing refresh...');
    window.location.reload();
  }, 3000);
  
  try {
    // Clear SW caches (with timeout)
    await Promise.race([
      clearServiceWorkerCaches(),
      new Promise(resolve => setTimeout(resolve, 1000))
    ]);
    
    // Unregister service workers (with timeout)
    await Promise.race([
      unregisterServiceWorkers(),
      new Promise(resolve => setTimeout(resolve, 1000))
    ]);
    
    // Clear app cache data
    clearAppCacheData();
    
    // Clear failsafe since we're about to refresh
    clearTimeout(failsafeTimeout);
    
    // Force hard refresh
    forceHardRefresh();
  } catch (error) {
    console.error('[CacheUtils] Error during cache clear:', error);
    clearTimeout(failsafeTimeout);
    // Refresh anyway
    window.location.reload();
  }
}

// Check if current SW is stale (different from active)
export async function isServiceWorkerStale(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;
    
    // If there's a waiting worker, update is available
    if (registration.waiting) return true;
    
    // If there's an installing worker, update is in progress
    if (registration.installing) return true;
    
    return false;
  } catch {
    return false;
  }
}

// Force SW update check
export async function checkForServiceWorkerUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      return !!registration.waiting || !!registration.installing;
    }
    return false;
  } catch {
    return false;
  }
}
